

/**
 * FloatingConnectionLine - Connection line that visually matches floating edges
 * Shows the line when the user is dragging to create a new connection.
 *
 * @module react-flow/edges/FloatingConnectionLine
 */

import { type ConnectionLineComponentProps, getBezierPath } from '@xyflow/react';
import { FC } from 'react';

import { getEdgeParams, type NodeLike } from './edge-utils';

/**
 * FloatingConnectionLine Component
 *
 * Usage:
 * ```tsx
 * <ReactFlow
 *   connectionLineComponent={FloatingConnectionLine}
 *   connectionLineStyle={{ stroke: '#b1b1b7' }}
 * />
 * ```
 */
export const FloatingConnectionLine: FC<ConnectionLineComponentProps> = ({
  toX,
  toY,
  fromNode,
  connectionLineStyle,
}) => {
  if (!fromNode) {
    return null;
  }

  // Create a temporary target node for calculating the edge params
  const targetNode: NodeLike = {
    position: { x: toX, y: toY },
    width: 1,
    height: 1,
  };

  // Cast fromNode to NodeLike for compatibility
  const { sourceX, sourceY, sourcePosition, targetPosition } = getEdgeParams(
    fromNode as unknown as NodeLike,
    targetNode
  );

  const [path] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX: toX,
    targetY: toY,
    targetPosition,
  });

  return (
    <g>
      <path
        fill="none"
        stroke="#b1b1b7"
        strokeWidth={2}
        d={path}
        style={connectionLineStyle}
      />
      <circle
        cx={toX}
        cy={toY}
        r={4}
        fill="#fff"
        stroke="#b1b1b7"
        strokeWidth={2}
      />
    </g>
  );
};

export default FloatingConnectionLine;
